import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';

export type OnboardingField = 'photo' | 'location' | 'interests' | 'phone' | 'dob' | 'verification';

interface OnboardingCoachContextType {
  missing: OnboardingField[];
  isEnsuring: boolean;
  ensure: (fields: OnboardingField[]) => Promise<boolean>;
  refreshMissing: () => void;
}

const OnboardingCoachContext = createContext<OnboardingCoachContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingCoachContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingCoachProvider');
  }
  return context;
};

interface OnboardingCoachProviderProps {
  children: React.ReactNode;
}

const ONBOARDING_FIELD_TO_SHEET_MAP: Record<OnboardingField, string> = {
  photo: 'addPhoto',
  location: 'enableLocation',
  interests: 'pickInterests',
  phone: 'addPhone',
  dob: 'addDob',
  verification: 'startVerification',
};

const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const OnboardingCoachProvider: React.FC<OnboardingCoachProviderProps> = ({ children }) => {
  const { currentUser, refreshCurrentUser } = useAuth();
  const [missingFields, setMissingFields] = useState<OnboardingField[]>([]);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const ensurePromiseRef = useRef<Promise<boolean> | null>(null);
  const hasTriggeredRef = useRef(false);

  // always-uptodate user snapshot
  const userRef = useRef<User | null>(currentUser);
  useEffect(() => {
    userRef.current = currentUser || null;
  }, [currentUser]);

  const computeMissingFields = useCallback((user: User | null): OnboardingField[] => {
    if (!user) return ['photo', 'interests', 'phone', 'dob', 'verification']; // assume all missing until we know
    const missing: OnboardingField[] = [];
    if (!user.avatarUrl) missing.push('photo');
    // Location is optional - removed from required fields
    // if (!user.latitude || !user.longitude || !user.location) missing.push('location');
    if (!user.interests || user.interests.length === 0) missing.push('interests');
    if (!user.phone) missing.push('phone');
    if (!user.dateOfBirth) missing.push('dob');
    if (!user.isVerified) missing.push('verification');
    return missing;
  }, []);

  useEffect(() => {
    setMissingFields(computeMissingFields(currentUser));
    
    // Reset trigger when user changes or logs out
    if (!currentUser) {
      hasTriggeredRef.current = false;
    }
  }, [currentUser, computeMissingFields]);

  const refreshMissing = useCallback(async () => {
    await refreshCurrentUser();
    // state effect will recalc
  }, [refreshCurrentUser]);

  // wait until an authenticated user object is available (or timeout)
  const waitForUserReady = useCallback(async (timeoutMs = 10000) => {
    const start = Date.now();
    while (!userRef.current) {
      if (Date.now() - start > timeoutMs) return false;
      await wait(100);
    }
    return true;
  }, []);

  const ensure = useCallback(
    async (fieldsToEnsure: OnboardingField[]): Promise<boolean> => {
      if (ensurePromiseRef.current) {
        console.warn('OnboardingCoach: ensure() already in progress. Returning existing promise.');
        return ensurePromiseRef.current;
      }

      setIsEnsuring(true);

      const api = (window as any).appSheets;
      if (!api || typeof api.open !== 'function') {
        console.error('OnboardingCoach: window.appSheets.open is not available. Is AppSheetsProvider mounted?');
        setIsEnsuring(false);
        return false;
      }

      const promise = (async () => {
        // make sure we have a user before starting the sequence
        const userReady = await waitForUserReady();
        if (!userReady) {
          console.warn('OnboardingCoach: Timed out waiting for user; aborting onboarding.');
          setIsEnsuring(false);
          ensurePromiseRef.current = null;
          return false;
        }

        let allCompleted = true;
        let snapshot = userRef.current;

        for (const field of fieldsToEnsure) {
          const currentMissing = computeMissingFields(snapshot);
          if (!currentMissing.includes(field)) {
            // already done — skip
            continue;
          }

          const sheetName = ONBOARDING_FIELD_TO_SHEET_MAP[field];
          if (!sheetName) {
            console.warn(`OnboardingCoach: No sheet mapped for field: ${field}`);
            continue;
          }
          
          // Bail if snapshot is not normalized yet
          if (!snapshot?.profileStatus) {
            console.warn('OnboardingCoach: user not normalized yet; aborting');
            await refreshCurrentUser();
            snapshot = userRef.current;
            if (!snapshot?.profileStatus) {
              console.warn('OnboardingCoach: user still not normalized after refresh; aborting');
              setIsEnsuring(false);
              ensurePromiseRef.current = null;
              return false;
            }
          }

          try {
            const ok = await api.open(sheetName);
            if (!ok) {
              allCompleted = false;
              break;
            }
            // pull fresh profile before the next step
            await refreshCurrentUser();
            await wait(50); // let state settle a tick
              
              // Check if snapshot is still valid after refresh
              if (!snapshot?.profileStatus) {
                console.warn('OnboardingCoach: user became unnormalized during flow; aborting');
                allCompleted = false;
                break;
              }
            snapshot = userRef.current;
          } catch (e) {
            console.error(`OnboardingCoach: error running sheet "${sheetName}"`, e);
            allCompleted = false;
            break;
          }
        }

        setIsEnsuring(false);
        ensurePromiseRef.current = null;
        return allCompleted;
      })();

      ensurePromiseRef.current = promise;
      return promise;
    },
    [computeMissingFields, refreshCurrentUser, waitForUserReady]
  );

  const contextValue = useMemo(
    () => ({
      missing: missingFields,
      isEnsuring,
      ensure,
      refreshMissing,
    }),
    [missingFields, isEnsuring, ensure, refreshMissing]
  );

  return (
    <OnboardingCoachContext.Provider value={contextValue}>
      {children}
    </OnboardingCoachContext.Provider>
  );
};
